'use client';

import { useEffect } from 'react';

export function ClientInit() {
  useEffect(() => {
    // Comprehensive RSC blocking for static export
    const originalFetch = window.fetch;
    const blockedUrls = new Set<string>();
    
    // Override fetch to completely block RSC requests
    window.fetch = function(...args) {
      const url = args[0];
      
      // More comprehensive RSC request blocking
      if (typeof url === 'string' && (
        url.includes('_rsc=') ||
        url.includes('.txt?') ||
        url.includes('/index.txt') ||
        url.endsWith('.txt') ||
        url.includes('__nextjs_original-stack-frame') ||
        url.includes('__nextjs_launch-editor')
      )) {
        if (!blockedUrls.has(url)) {
          blockedUrls.add(url);
          // Only log in development to reduce console noise
          if (process.env.NODE_ENV === 'development') {
            console.warn(`[RSC-Block] ${url.split('?')[0]}`);
          }
        }
        
        // Return successful empty response to prevent 404 errors
        return Promise.resolve(new Response('null', { 
          status: 200,
          headers: { 
            'content-type': 'application/json',
            'cache-control': 'no-cache'
          }
        }));
      }
      
      return originalFetch.apply(this, args);
    };
    
    // Disable Next.js prefetching at the router level
    if (typeof window !== 'undefined') {
      // Disable router prefetch
      type WindowWithNextData = Window & {
        __NEXT_DATA__?: {
          rsc?: boolean;
          buildManifest?: {
            polyfillFiles?: string[];
          };
        };
      };
      const windowWithData = window as WindowWithNextData;
      const nextData = windowWithData.__NEXT_DATA__;
      if (nextData) {
        nextData.rsc = false;
        nextData.buildManifest = nextData.buildManifest || {};
        nextData.buildManifest.polyfillFiles = [];
      }
      
      // Disable prefetch on all Link components
      const style = document.createElement('style');
      style.textContent = `
        [data-nextjs-scroll-focus-boundary] { outline: none !important; }
        .next-error-h1 { display: none !important; }
      `;
      document.head.appendChild(style);
      
      // Listen for router events and disable prefetch
      let routerInitAttempts = 0;
      const disableRouterPrefetch = () => {
        if (routerInitAttempts > 50) {
          return; // Prevent infinite loop
        }
        
        type RouterWindow = Window & {
          next?: {
            router?: {
              prefetch?: () => Promise<void>;
              events?: {
                off?: (event: string, handler: () => void) => void;
              };
            };
          };
          __NEXT_ROUTER__?: {
            prefetch?: () => Promise<void>;
            events?: {
              off?: (event: string, handler: () => void) => void;
            };
          };
        };
        const windowWithRouter = window as RouterWindow;
        const router = windowWithRouter.next?.router || windowWithRouter.__NEXT_ROUTER__;
        if (router && router.prefetch) {
          router.prefetch = () => Promise.resolve();
          router.events?.off?.('routeChangeComplete', disableRouterPrefetch);
        } else {
          routerInitAttempts++;
          setTimeout(disableRouterPrefetch, 100);
        }
      };
      
      disableRouterPrefetch();
    }
    
    // Block all RSC-related network activity
    const originalXHROpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(
      method: string, 
      url: string | URL, 
      async: boolean = true,
      username?: string | null,
      password?: string | null
    ) {
      const urlString = typeof url === 'string' ? url : url.toString();
      if (urlString.includes('_rsc=') || urlString.includes('.txt')) {
        // Return a no-op URL to prevent the request
        return originalXHROpen.call(this, method, 'data:text/plain,', async, username, password);
      }
      return originalXHROpen.call(this, method, url, async, username, password);
    };
    
    // Clean up any existing RSC artifacts
    const cleanupRSC = () => {
      // Remove RSC scripts
      document.querySelectorAll('script[src*="_rsc"], script[src*=".txt"]').forEach(script => {
        script.remove();
      });
      
      // Remove RSC stylesheets
      document.querySelectorAll('link[href*="_rsc"], link[href*=".txt"]').forEach(link => {
        link.remove();
      });
    };
    
    cleanupRSC();
    
    // Cleanup on page navigation
    const observer = new MutationObserver(cleanupRSC);
    observer.observe(document.head, { childList: true });
    
    return () => {
      observer.disconnect();
    };
  }, []);

  return null;
}